import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as z from "zod";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { X, Search, UserPlus, ChevronDown } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useUsers } from "@/hooks/users/useUsers";
import { useStates } from "@/hooks/data/useStates";
import { useClinics } from "@/hooks/clinics/useClinics";
import LoaderComponent from "@/components/common/LoaderComponent";
import PaginationComponent from "@/components/common/PaginationComponent";
import type { UserDashboard } from "@/services/api/users/usersGraphqlService";


const clinicUserGroupFormSchema = z.object({
  groupName: z
    .string()
    .min(1, "Group name is required")
    .max(100, "Group name must be less than 100 characters")
    .regex(
      /^[a-zA-Z0-9\s\-_]+$/,
      "Group name can only contain letters, numbers, spaces, hyphens, and underscores"
    ),
});

type ClinicUserGroupFormData = z.infer<typeof clinicUserGroupFormSchema>;

interface ClinicUserGroupFormProps {
  initialData?: {
    groupName?: string;
    assignedUserIds?: number[];
    stateId?: number;
    clinicIds?: number[];
  };
  initialUsers?: { userId: number; userName: string; appUserName: string | null; userType?: string }[];
  title: string;
  onSubmit: (data: {
    groupName: string;
    assignedUsers: UserDashboard[];
    stateId?: number;
    clinicIds?: number[];
  }) => void;
  isSubmitting?: boolean;
  existingGroupId?: number;
}

export const ClinicUserGroupForm = ({
  initialData,
  initialUsers,
  title,
  onSubmit,
  isSubmitting,
  existingGroupId,
}: ClinicUserGroupFormProps) => {
  const isEditMode = !!existingGroupId;
  const navigate = useNavigate();

  // State/Clinic selection
  const [selectionType, setSelectionType] = useState<"clinic" | "state">(
    initialData?.stateId ? "state" : "clinic"
  );
  const [selectedStateId, setSelectedStateId] = useState<number | undefined>(initialData?.stateId);
  const [selectedClinicIds, setSelectedClinicIds] = useState<number[]>(initialData?.clinicIds || []);

  // Healthcare centers (states) and clinics
  const { states, isLoading: statesLoading } = useStates();
  const { clinics, loading: clinicsLoading, searchClinics } = useClinics(1000);

  // Users modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<UserDashboard[]>([]);
  const [tempSelectedUsers, setTempSelectedUsers] = useState<UserDashboard[]>([]);
  const [loadingInitialUsers, setLoadingInitialUsers] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    users,
    loading,
    currentPage,
    totalPages,
    totalCount,
    entriesPerPage,
    filterType,
    searchValue,
    setCurrentPage,
    setEntriesPerPage,
    setFilterType,
    setSearchValue,
    handleSearch,
    clearSearch,
  } = useUsers();

  const form = useForm<ClinicUserGroupFormData>({
    resolver: zodResolver(clinicUserGroupFormSchema),
    mode: "onChange",
    defaultValues: {
      groupName: initialData?.groupName || "",
    },
  });

  const watchedGroupName = form.watch("groupName");

  // Stable initial data for dirty check
  const initialDataRef = useMemo(() => ({
    groupName: initialData?.groupName || "",
    assignedUserIds: initialData?.assignedUserIds || [],
    stateId: initialData?.stateId,
    clinicIds: initialData?.clinicIds || [],
  }), []);

  const hasFormChanges = useCallback(() => {
    if (!isEditMode) return true;
    if ((watchedGroupName?.trim() || '') !== initialDataRef.groupName.trim()) return true;
    
    const currentUserIds = selectedUsers.map(u => u.id).sort((a, b) => a - b);
    const initialUserIds = [...initialDataRef.assignedUserIds].sort((a, b) => a - b);
    if (currentUserIds.length !== initialUserIds.length) return true;
    for (let i = 0; i < currentUserIds.length; i++) {
      if (currentUserIds[i] !== initialUserIds[i]) return true;
    }

    if (selectedStateId !== initialDataRef.stateId) return true;
    const currentClinics = [...selectedClinicIds].sort((a, b) => a - b);
    const initialClinics = [...initialDataRef.clinicIds].sort((a, b) => a - b);
    if (currentClinics.length !== initialClinics.length) return true;
    for (let i = 0; i < currentClinics.length; i++) {
      if (currentClinics[i] !== initialClinics[i]) return true;
    }

    return false;
  }, [watchedGroupName, selectedUsers, selectedStateId, selectedClinicIds, isEditMode, initialDataRef]);

  const formHasChanges = hasFormChanges();

  // Populate users from initialUsers prop (edit mode)
  useEffect(() => {
    if (initialUsers && initialUsers.length > 0 && selectedUsers.length === 0) {
      const mappedUsers: UserDashboard[] = initialUsers.map(u => ({
        id: u.userId,
        user_name: u.appUserName || u.userName,
        email: u.userName,
        user_type: u.userType || '',
        is_active: true,
        phone_number: '',
        updated: '',
        last_active: '',
      }));
      setSelectedUsers(mappedUsers);
    }
  }, [initialUsers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Radio-based selection type
  const isStateDisabled = selectionType !== "state";
  const isClinicsDisabled = selectionType !== "clinic";

  const handleSelectionTypeChange = (value: string) => {
    setSelectionType(value as "clinic" | "state");
    if (value === "clinic") {
      setSelectedStateId(undefined);
    } else {
      setSelectedClinicIds([]);
      setClinicPanelOpen(false);
    }
  };

  const handleStateChange = (value: string) => {
    try {
      setSelectedStateId(Number(value));
      setSelectedClinicIds([]);
    } catch (error) {
      console.error('Error handling state change:', error);
    }
  };

  const handleClinicToggle = (clinicId: number) => {
    try {
      setSelectedClinicIds(prev => {
        if (prev.includes(clinicId)) {
          return prev.filter(id => id !== clinicId);
        }
        return [...prev, clinicId];
      });
      setSelectedStateId(undefined); // Clear state when clinic is selected
    } catch (error) {
      console.error('Error handling clinic toggle:', error);
    }
  };

  const handleRemoveClinic = (clinicId: number) => {
    setSelectedClinicIds(prev => prev.filter(id => id !== clinicId));
  };

  // Users modal logic (same as UserGroupForm)
  const availableUsers = users.filter(user =>
    !selectedUsers.some(selected => selected.id === user.id)
  );

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!isModalOpen) return;
    if (searchValue.length === 0) { 
      try { clearSearch(); } catch (e) { console.error('Error clearing search:', e); }
      return; 
    }
    if (searchValue.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        try { handleSearch(); } catch (e) { console.error('Error in search:', e); }
      }, 300);
    }
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchValue, isModalOpen]);

  useEffect(() => {
    if (isModalOpen && searchValue.length >= 2) handleSearch();
  }, [filterType]);

  const handleOpenModal = () => {
    setTempSelectedUsers([]);
    setSearchValue("");
    setFilterType("user_name");
    setCurrentPage(1);
    setIsModalOpen(true);
    setTimeout(() => handleSearch(), 0);
  };

  const handleModalUserSelect = (user: UserDashboard) => {
    setTempSelectedUsers(prev => {
      const isSelected = prev.some(s => s.id === user.id);
      return isSelected ? prev.filter(s => s.id !== user.id) : [...prev, user];
    });
  };

  const handleAddUsers = () => {
    setSelectedUsers(prev => [...prev, ...tempSelectedUsers]);
    setIsModalOpen(false);
    setTempSelectedUsers([]);
  };

  const handleUserRemove = (userId: number) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
  };

  const handleSubmit = (data: ClinicUserGroupFormData) => {
    if (selectedUsers.length < 1) {
      form.setError("groupName", { type: "manual", message: "At least 1 user must be added to the group" });
      return;
    }

    if (!selectedStateId && selectedClinicIds.length === 0) {
      form.setError("groupName", { type: "manual", message: "Please select either a state or at least one clinic" });
      return;
    }

    onSubmit({
      groupName: data.groupName.trim(),
      assignedUsers: selectedUsers,
      stateId: selectedStateId,
      clinicIds: selectedClinicIds.length > 0 ? selectedClinicIds : undefined,
    });
  };

  const handleCancel = () => {
    navigate("/admin/clinic-user-groups");
  };

  // Clinic search state
  const [clinicSearchQuery, setClinicSearchQuery] = useState("");
  const [clinicPanelOpen, setClinicPanelOpen] = useState(false);
  const filteredClinics = clinicSearchQuery
    ? clinics.filter(c => c.clinic_name.toLowerCase().includes(clinicSearchQuery.toLowerCase()))
    : clinics;

  if (loadingInitialUsers) {
    return (
      <div className="container max-w-4xl mx-auto p-6">
        <Card><CardContent className="p-6"><div className="flex justify-center py-8"><LoaderComponent /></div></CardContent></Card>
      </div>
    );
  }

  return (
    <div id="clinic_user_group_form_container" className="container max-w-4xl mx-auto p-6 pb-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-semibold text-foreground">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form id="clinic_user_group_form" onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
              {/* Group Name */}
              <FormField
                control={form.control}
                name="groupName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Clinic User Group Name *</FormLabel>
                    <FormControl>
                      <Input
                        id="clinic_user_group_form_name_input"
                        placeholder="Enter group name"
                        {...field}
                        className="bg-background border-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Radio Selection: Clinic or State */}
              <div className="space-y-4">
                <RadioGroup
                  value={selectionType}
                  onValueChange={handleSelectionTypeChange}
                  className="grid grid-cols-2 gap-4"
                >
                  {/* Clinic Option */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="clinic" id="selection-clinic" />
                      <Label htmlFor="selection-clinic" className="text-sm font-medium cursor-pointer">
                        Clinic<span className="text-destructive">*</span>
                      </Label>
                    </div>
                    <div className={selectionType !== "clinic" ? "opacity-50 pointer-events-none" : ""}>
                      <button
                        type="button"
                        disabled={selectionType !== "clinic"}
                        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={() => setClinicPanelOpen(prev => !prev)}
                      >
                        <span className={selectedClinicIds.length > 0 ? "text-foreground" : "text-muted-foreground"}>
                          {selectedClinicIds.length > 0
                            ? `${selectedClinicIds.length} clinic(s) selected`
                            : "Select Clinic"}
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </button>
                    </div>
                  </div>

                  {/* State Option */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="state" id="selection-state" />
                      <Label htmlFor="selection-state" className="text-sm font-medium cursor-pointer">
                        State
                      </Label>
                    </div>
                    <div className={selectionType !== "state" ? "opacity-50 pointer-events-none" : ""}>
                      <Select
                        key={selectionType === "state" ? "state-select" : "state-select-reset"}
                        disabled={selectionType !== "state"}
                        value={selectedStateId !== undefined && selectedStateId !== null ? selectedStateId.toString() : undefined}
                        onValueChange={handleStateChange}
                      >
                        <SelectTrigger id="clinic_user_group_form_state_select" className="bg-background border-input">
                          <SelectValue placeholder="Select State" />
                        </SelectTrigger>
                        <SelectContent className="bg-background">
                          {states.map(state => (
                            <SelectItem key={state.id} value={state.id.toString()}>
                              {state.state_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </RadioGroup>

                {/* Clinic multi-select panel (shown when clinic radio is selected) */}
                {selectionType === "clinic" && clinicPanelOpen && (
                  <div className="space-y-2">
                    {selectedClinicIds.length > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">{selectedClinicIds.length} selected</span>
                        <button
                          type="button"
                          className="text-xs text-primary hover:text-primary/80 underline"
                          onClick={() => setSelectedClinicIds([])}
                        >
                          Clear all
                        </button>
                      </div>
                    )}

                    {/* Selected clinics badges */}
                    {selectedClinicIds.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {selectedClinicIds.map(id => {
                          const clinic = clinics.find(c => c.clinic_id === id);
                          return (
                            <Badge key={id} variant="secondary" className="flex items-center gap-1">
                              {clinic?.clinic_name || `Clinic ${id}`}
                              <button type="button" onClick={() => handleRemoveClinic(id)} className="ml-1 hover:text-destructive">
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}

                    <div className="border border-input rounded-md bg-background">
                      <div className="p-2 border-b border-input">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                          <Input
                            id="clinic_user_group_form_clinic_search"
                            placeholder="Search clinics..."
                            value={clinicSearchQuery}
                            onChange={(e) => setClinicSearchQuery(e.target.value)}
                            className="pl-10 bg-background border-input h-8"
                          />
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {clinicsLoading ? (
                          <div className="flex justify-center py-4"><LoaderComponent /></div>
                        ) : filteredClinics.length > 0 ? (
                          filteredClinics.map(clinic => {
                            const isChecked = selectedClinicIds.includes(clinic.clinic_id);
                            return (
                              <div
                                key={clinic.clinic_id}
                                className="flex items-center gap-3 px-4 py-2 hover:bg-accent cursor-pointer"
                                onClick={() => {
                                  try {
                                    handleClinicToggle(clinic.clinic_id);
                                  } catch (err) {
                                    console.error('Error toggling clinic:', err);
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => {}}
                                  className="h-4 w-4 shrink-0 rounded-sm border border-primary accent-primary"
                                />
                                <span className="text-sm text-foreground">{clinic.clinic_name || `Clinic ${clinic.clinic_id}`}</span>
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-sm text-muted-foreground p-4">No clinics found</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Users in this group */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <FormLabel>Users in this group ({selectedUsers.length}) *</FormLabel>
                  <Button
                    id="clinic_user_group_form_add_users_btn"
                    type="button"
                    variant="tertiary"
                    size="sm"
                    onClick={handleOpenModal}
                    className="flex items-center gap-2"
                  >
                    <UserPlus className="h-4 w-4" />
                    Add Users
                  </Button>
                </div>
                {selectedUsers.length < 1 && (
                  <p className="text-sm text-destructive">At least 1 user is required</p>
                )}

                <div id="clinic_user_group_form_users_list" className="border border-input rounded-md bg-background">
                  <div className="max-h-48 overflow-y-auto">
                    {selectedUsers.length > 0 ? (
                      selectedUsers.map((user, index) => (
                        <div
                          id={`clinic_user_group_form_user_row_${user.id}`}
                          key={user.id}
                          className={`flex items-center justify-between hover:bg-accent py-3 px-4 ${index !== selectedUsers.length - 1 ? 'border-b' : ''}`}
                        >
                          <div>
                            <p className="text-sm font-medium text-foreground">{user.user_name}</p>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {user.user_type && <Badge variant="secondary">{user.user_type}</Badge>}
                            <Button
                              id={`clinic_user_group_form_remove_user_${user.id}_btn`}
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUserRemove(user.id)}
                              className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground p-4">
                        No users assigned to this group. Start{" "}
                        <a href="#" onClick={handleOpenModal} className="text-secondary underline">adding users</a>.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 justify-end">
                <Button
                  id="clinic_user_group_form_cancel_btn"
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  id="clinic_user_group_form_submit_btn"
                  type="submit"
                  disabled={isSubmitting || !form.formState.isValid || selectedUsers.length < 1 || (isEditMode && !formHasChanges)}
                  className="bg-primary text-primary-foreground hover:bg-primary-hover"
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Add Users Modal - same as UserGroupForm */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent id="clinic_user_group_add_users_dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Users to Group</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  id="clinic_user_group_modal_search_input"
                  placeholder="Search (min 2 characters)"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  className="pl-10 bg-background border-input"
                />
              </div>
              <Select value={filterType} onValueChange={(value) => setFilterType(value as any)}>
                <SelectTrigger className="bg-background border-input">
                  <SelectValue placeholder="Search by" />
                </SelectTrigger>
                <SelectContent className="bg-background">
                  <SelectItem value="user_name">Username</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="user_type">User Type</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border border-input rounded-md p-4 bg-background">
              <h4 className="text-sm font-medium text-foreground mb-3">Select Users ({totalCount} total)</h4>
              {loading ? (
                <div className="flex justify-center py-8"><LoaderComponent /></div>
              ) : (
                <>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {availableUsers.length > 0 ? (
                      availableUsers.map(user => {
                        const isSelected = tempSelectedUsers.some(s => s.id === user.id);
                        return (
                          <div
                            key={user.id}
                            className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-colors ${
                              isSelected ? 'bg-primary/10 border border-primary' : 'hover:bg-accent'
                            }`}
                            onClick={() => handleModalUserSelect(user)}
                          >
                            <div className="flex items-center gap-3">
                              <input type="checkbox" checked={isSelected} onChange={() => {}} className="rounded border-input" />
                              <div>
                                <p className="text-sm font-medium text-foreground">{user.user_name}</p>
                                <p className="text-xs text-muted-foreground">{user.email}</p>
                              </div>
                            </div>
                            {user.user_type && <Badge variant="secondary">{user.user_type}</Badge>}
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-muted-foreground">No users available</p>
                    )}
                  </div>
                  {totalPages > 1 && (
                    <div className="mt-4 pt-4 border-t border-input">
                      <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        recordsPerPage={entriesPerPage}
                        totalRecords={totalCount}
                        startIndex={(currentPage - 1) * entriesPerPage + 1}
                        endIndex={Math.min(currentPage * entriesPerPage, totalCount)}
                        onPageChange={setCurrentPage}
                        onRecordsPerPageChange={setEntriesPerPage}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAddUsers}
              disabled={tempSelectedUsers.length === 0}
              className="bg-primary text-primary-foreground hover:bg-primary-hover"
            >
              Add ({tempSelectedUsers.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
